import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage, type AppLanguage } from '../../contexts/LanguageContext';
import { getLanguageEndonym, LANGUAGE_OPTIONS } from '../../i18n/constants';
import ConfirmDialog from '../ui/ConfirmDialog';
import { fetchNotificationCounts } from '../../services/notifications';
import {
  getInternalUserProfileById,
  getSwitchRoles,
  switchUserRole,
  type InternalUserProfileRole,
} from '../../services/userProfile';
import { getAppUserId, getStoredActiveAppUser, persistActiveAppUser } from '../../services/activeAppUser';
import { useAccessControl } from '../../contexts/AccessControlContext';
import { destroyRbacSession } from '../../rbac/permissionService';
import { markRoleJustSwitched } from '../../rbac/roleSwitchFlag';
import type { User } from '../../services/types';
import { useDmsImageObjectUrl } from '../../hooks/useDmsImageObjectUrl';
import { AUTH_STORAGE_EVENT, getStorage, setStorage } from '../../utils/authStorage';
import {
  NOTIFICATION_COUNTS_UPDATED,
  type NotificationCountsDetail,
} from '../../utils/notificationEvents';
import { CaseScenariosInfoPanel } from './CaseScenariosInfoPanel';

function formatBadgeCount(count: number): string {
  if (count > 99) return '99+';
  return String(count);
}

export function TopHeader({ subtitle }: { subtitle: string }) {
  const { user, signOut } = useAuth();
  const { refresh: refreshAccessControl } = useAccessControl();
  const { theme, setMode, setPrimary, reset } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [roles, setRoles] = useState<InternalUserProfileRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [appUserId, setAppUserId] = useState(() => getAppUserId() ?? '');
  const [profilePicRef, setProfilePicRef] = useState('');
  const [profilePicFullpath, setProfilePicFullpath] = useState('');
  const avatarUrl = useDmsImageObjectUrl(profilePicRef, profilePicFullpath);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    let controller = new AbortController();

    const loadUnreadCount = (event?: Event) => {
      const detail = (event as CustomEvent<NotificationCountsDetail> | undefined)?.detail;
      if (detail && typeof detail.unreadCount === 'number') {
        setUnreadCount(Math.max(0, detail.unreadCount));
        return;
      }

      controller.abort();
      controller = new AbortController();
      void fetchNotificationCounts(controller.signal).then((res) => {
        if (controller.signal.aborted) return;
        if (res.success) setUnreadCount(Math.max(0, res.data.unreadCount));
      });
    };

    loadUnreadCount();
    window.addEventListener(NOTIFICATION_COUNTS_UPDATED, loadUnreadCount);
    window.addEventListener('focus', loadUnreadCount);

    return () => {
      controller.abort();
      window.removeEventListener(NOTIFICATION_COUNTS_UPDATED, loadUnreadCount);
      window.removeEventListener('focus', loadUnreadCount);
    };
  }, [user]);
  useEffect(() => {
    if (!settingsOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest?.('.hdr-gi')) setSettingsOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [settingsOpen]);

  useEffect(() => {
    if (!accountOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest?.('.hdr-acct')) setAccountOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [accountOpen]);

  useEffect(() => {
    if (!user) {
      setRoles([]);
      setSelectedRole('');
      return;
    }
    let alive = true;
    setRolesLoading(true);
    setSwitchError(null);
    void getSwitchRoles()
      .then((res) => {
        if (!alive) return;
        if (res.success) {
          setRoles(res.data);
          setSelectedRole('');
          if (res.data.length === 0) setSwitchError(t('header.noRolesAvailable'));
        } else {
          setSwitchError(res.message);
        }
      })
      .finally(() => {
        if (alive) setRolesLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [user?.id, t]);

  useEffect(() => {
    const sync = () => setAppUserId(getAppUserId() ?? '');
    sync();
    window.addEventListener(AUTH_STORAGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(AUTH_STORAGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    const id = appUserId.trim();
    if (!user || !id) {
      setProfilePicRef('');
      setProfilePicFullpath('');
      return;
    }
    let alive = true;
    void getInternalUserProfileById(id).then((res) => {
      if (!alive) return;
      if (res.success) {
        setProfilePicRef(res.data.profilePictureIconUrl);
        setProfilePicFullpath(res.data.profilePictureIconUrlFullpath);
      }
    });
    return () => {
      alive = false;
    };
  }, [user?.id, appUserId]);

  const runSwitchRole = async () => {
    const roleId = Number(selectedRole);
    if (!Number.isFinite(roleId) || roleId <= 0) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      const res = await switchUserRole(roleId);
      if (!res.success) {
        setSwitchError(res.message || t('header.switchRoleFailed'));
        return;
      }

      // Frontend-only: apply the switched role to the local session + permissions.
      // The backend /active endpoint still returns the default role, so we patch the
      // stored active user with the selected role rather than re-syncing from it.
      const newRoleName = roles.find((r) => r.roleId === roleId)?.roleName ?? '';
      const storedActive = getStoredActiveAppUser();
      if (storedActive) {
        persistActiveAppUser({
          ...storedActive,
          defaultRoleId: roleId,
          defaultRole: newRoleName || storedActive.defaultRole,
          roleDescription: newRoleName || storedActive.roleDescription,
        });
      }

      // Rebuild RBAC permissions for the newly selected role. Ticket 0002137: if this
      // invalidates the screen we're currently on, RequireAccess (guards.tsx) redirects
      // to the new role's own landing page itself — flagged here so it knows this
      // denial came from a switch (silent redirect home) and not a direct navigation
      // to a page this role never had access to (/unauthorized instead).
      markRoleJustSwitched();
      destroyRbacSession();
      await refreshAccessControl();

      // Update the header role label only after permissions refresh so label and access stay in sync.
      const cur = getStorage<User>('kcUser');
      setStorage('kcUser', {
        id: cur?.id ?? (getAppUserId() ?? ''),
        displayName: cur?.displayName ?? 'User',
        role: (newRoleName || cur?.role || 'Assigned Officer') as User['role'],
      });

      // Reload the switchable-roles list so it reflects the new current role.
      const rolesRes = await getSwitchRoles();
      if (rolesRes.success) {
        setRoles(rolesRes.data);
        if (rolesRes.data.length === 0) setSwitchError(t('header.noRolesAvailable'));
      }
      setSelectedRole('');
      setAccountOpen(false);
    } catch {
      setSwitchError(t('header.switchRoleFailed'));
    } finally {
      setSwitching(false);
    }
  };

  // The backend "switchable roles" query only excludes the current role when
  // currentRoleId is set; for a user who has never switched (currentRoleId IS NULL)
  // it returns their own single role too. Exclude the active/default role here so
  // the "Switch Role" option is shown only when there is another role to switch to.
  const currentRoleId = getStoredActiveAppUser()?.defaultRoleId ?? 0;
  const switchableRoles = roles.filter((r) => r.roleId !== currentRoleId);

  return (
    <div className="top-h">
      <div className="hdr-brand">
        <div className="hdr-logo">
          <img className="hdr-logo-img" src="/AGD Logo.png" alt={t('header.systemName')} />
        </div>
        <div className="hdr-brand-text">
          <div className="hdr-sys-name">{t('header.systemName')}</div>
          <div className="hdr-sys-sub">{subtitle}</div>
        </div>
      </div>

      <div className="hdr-right">
        <Link className="bell-wrap" to="/notifications" aria-label={t('header.notifications')}>
          <button className="bell-b" type="button" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </button>
          <span className="bell-badge" aria-hidden={unreadCount <= 0} style={unreadCount <= 0 ? { display: 'none' } : undefined}>
            {formatBadgeCount(unreadCount)}
          </span>
        </Link>

        <div className="hdr-acct">
          <button
            type="button"
            className="user-section"
            onClick={() => setAccountOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            aria-label={t('header.accountMenu')}
          >
            <div className="user-avatar" aria-hidden="true">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit', display: 'block' }}
                />
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              )}
            </div>
            <div className="user-meta">
              <div className="user-name">{user?.displayName ?? '—'}</div>
              <div className="user-role">{user?.role || (user ? t('common.loading') : '—')}</div>
            </div>
            <svg
              className={`chevron acct-chevron${accountOpen ? ' open' : ''}`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {accountOpen ? (
            <div className="hdr-ddm hdr-acct-ddm" role="menu" aria-label={t('header.accountMenu')}>
              <Link className="acct-item" to="/profile" role="menuitem" onClick={() => setAccountOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>{t('header.profile')}</span>
              </Link>

              {switchableRoles.length > 0 ? (
              <div className="acct-section">
                <div className="acct-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <span>{t('header.switchRole')}</span>
                </div>
                <div className="acct-switch-row">
                  <select
                    className={`hdr-sel acct-role-sel${selectedRole ? '' : ' is-placeholder'}`}
                    value={selectedRole}
                    onChange={(e) => {
                      setSelectedRole(e.target.value);
                      setSwitchError(null);
                    }}
                    disabled={rolesLoading || switching}
                    aria-label={t('header.selectUserRole')}
                  >
                    <option value="" disabled hidden>
                      {rolesLoading ? t('common.loading') : t('header.selectUserRole')}
                    </option>
                    {switchableRoles.map((r) => (
                      <option key={r.roleId} value={String(r.roleId)}>
                        {r.roleName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="acct-switch-btn"
                    disabled={!selectedRole || rolesLoading || switching}
                    onClick={() => void runSwitchRole()}
                  >
                    {switching ? t('common.loading') : t('header.switch')}
                  </button>
                </div>
                {switchError ? <div className="acct-switch-err">{switchError}</div> : null}
              </div>
              ) : null}

              <button
                type="button"
                className="acct-item acct-signout"
                role="menuitem"
                onClick={() => {
                  setAccountOpen(false);
                  setLogoutOpen(true);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>{t('header.signOut')}</span>
              </button>
            </div>
          ) : null}
        </div>

        <div className="hdr-gi">
          <button
            className="hdr-icon-btn"
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={settingsOpen}
            aria-label={t('header.settings')}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.65"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
          {settingsOpen ? (
            <div className="hdr-ddm" role="menu" aria-label={t('common.settingsMenu')}>
              <h4>{t('settings.theme')}</h4>
              <div className="hdr-row">
                <label htmlFor="theme-mode">{t('settings.mode')}</label>
                <select id="theme-mode" className="hdr-sel" value={theme.mode} onChange={(e) => setMode(e.target.value === 'dark' ? 'dark' : 'light')}>
                  <option value="light">{t('settings.light')}</option>
                  <option value="dark">{t('settings.dark')}</option>
                </select>
              </div>
              <div className="hdr-row">
                <label htmlFor="theme-primary">{t('settings.primary')}</label>
                <input
                  id="theme-primary"
                  className="hdr-color"
                  type="color"
                  value={theme.primary}
                  onChange={(e) => setPrimary(e.target.value)}
                  aria-label={t('settings.primaryColor')}
                />
              </div>
              <button className="hdr-link" type="button" onClick={() => reset()}>
                {t('settings.resetTheme')}
              </button>

              <hr className="hdr-ddm-divider" aria-hidden="true" />
              <h4>{t('settings.language')}</h4>
              <div className="hdr-row">
                <label htmlFor="lang-sel">{t('settings.language')}</label>
                <select
                  id="lang-sel"
                  className="hdr-sel"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as AppLanguage)}
                >
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {getLanguageEndonym(opt.code)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}
        </div>

        <Link className="hdr-icon-btn" to="/case-scenarios" aria-label={t('header.info')} title={t('header.info')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </Link>
      </div>

      <ConfirmDialog
        isOpen={logoutOpen}
        title={t('common.logout')}
        message={t('common.logoutConfirm')}
        cancelLabel={t('common.cancel')}
        confirmLabel={t('common.logout')}
        onClose={() => setLogoutOpen(false)}
        onConfirm={() => {
          setLogoutOpen(false);
          console.info('[ui] logout confirmed');
          void signOut();
        }}
      />

      <CaseScenariosInfoPanel open={infoPanelOpen} onClose={() => setInfoPanelOpen(false)} />
    </div>
  );
}

